import React, {useState, useEffect, ChangeEvent} from 'react';
import axios from 'axios';
import { Container, Typography, Box, Paper, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Link, Grid as Grid } from '@mui/material';
import { Edit, Delete, Campaign } from '@mui/icons-material';
import AdUploader from '../components/AdUploader';

export default function AdsPage() {
    const [ads, setAds] = useState<any[]>([]);
    const [editingAd, setEditingAd] = useState<any | null>(null);
    const [isUploadingAd, setIsUploadingAd] = useState<boolean>(false);
    const [adPreview, setAdPreview] = useState<string | null>(null);
    const [adImage, setAdImage] = useState<File | null>(null);

    const poppins = { fontFamily: "'Poppins', sans-serif" };

    const fetchAds = async () => {
        try {
            const res = await axios.get('https://api.237showbiz.com/api/ads');
            setAds(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Fetch error:", err);
        }
    };

    useEffect(() => {
        fetchAds();
    }, []);
    const handleClearAd = () => {
        if (adPreview) URL.revokeObjectURL(adPreview);
        setAdImage(null);
        setAdPreview(null);
        setEditingAd(null);
    };
    const handleAdSubmitAction = async (payload: { adUrl: string | null; lifetimeMinutes: number | null }) => {
        setIsUploadingAd(true);
        try {
            if (editingAd) {
                // =========================
                // UPDATE EXISTING (PUT)
                // =========================
                console.log("Updating existing ad:", editingAd.ad_id);

                // Note: Use JSON for updates unless your backend explicitly expects FormData
                const response = await axios.put("https://api.237showbiz.com/api/ads", {
                    ad_id: editingAd.ad_id,
                    redirect_url: payload.adUrl,
                    lifetime_minutes: payload.lifetimeMinutes
                });

                if (response.data.success) {
                    alert("✅ Ad updated successfully!");
                    setEditingAd(null); // Triggers UI switch to 'Launch Campaign'
                    handleClearAd();
                    await fetchAds();
                } else {
                    throw new Error(response.data.error || "Update failed.");
                }
            } else {
                // =========================
                // CREATE NEW (POST)
                // =========================
                const formData = new FormData();
                if (adImage) formData.append("image", adImage);
                if (payload.adUrl) formData.append("redirect_url", payload.adUrl.trim());
                if (payload.lifetimeMinutes) formData.append("lifetime_minutes", payload.lifetimeMinutes.toString());

                const response = await axios.post("https://api.237showbiz.com/api/ads", formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                });

                if (response.data.success) {
                    alert("✅ Ad launched successfully!");
                    handleClearAd();
                    await fetchAds();
                } else {
                    throw new Error(response.data.error || "Upload failed.");
                }
            }
        } catch (err: any) {
            console.error("Submission error:", err);
            alert(err.response?.data?.error || err.message || "An error occurred.");
        } finally {
            setIsUploadingAd(false);
        }
    };

    const handleEditAd = (ad: any) => {
        setEditingAd(ad);
        setAdPreview(ad.file_path);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteAd = async (id: string) => {
        if (!window.confirm("Delete this ad?")) return;
        await axios.delete('https://api.237showbiz.com/api/ads', { data: { ad_id: id } });
        fetchAds();
    };

    const handleAdImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setAdImage(file);
            setAdPreview(URL.createObjectURL(file));
        }
    };

    return (
        <Container sx={{ py: 4 }}>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 800, ...poppins }}>Ads Management</Typography>

            <Grid size={{ xs: 12 }}>
                <AdUploader
                    preview={adPreview}
                    fileType={adImage?.type || null}
                    onImageChange={handleAdImageChange}
                    onClearMedia={handleClearAd}
                    onSubmitAd={handleAdSubmitAction}
                    currentAd={editingAd}
                    poppins={poppins}
                    isUploadingAd={isUploadingAd}
                />
            </Grid>

            <Grid size={{ xs: 12 }}>
                <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #E9EDF7', overflow: 'hidden', bgcolor: 'white', mt: 4 }}>
                    <Box p={4} display="flex" alignItems="center" gap={2}>
                        <Box sx={{ p: 1, bgcolor: '#E1E9F8', borderRadius: '10px' }}>
                            <Campaign sx={{ color: '#1B2559' }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1B2559', ...poppins }}>
                            Live Ad Campaign Registry
                        </Typography>
                    </Box>
                    <Divider />
                    <TableContainer sx={{ maxHeight: 320 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>AD ID</TableCell>
                                    <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>PREVIEW</TableCell>
                                    <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>DESTINATION URL</TableCell>
                                    <TableCell align="center" sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>ACTIONS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.isArray(ads) && ads.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#A3AED0', ...poppins }}>
                                            No active ad campaigns.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    ads.map((ad) => (
                                        <TableRow key={ad.ad_id} hover>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', ...poppins }}>
                                                {ad.ad_id.substring(0, 8)}...
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ width: 80, height: 60, borderRadius: '8px', overflow: 'hidden', bgcolor: '#F4F7FE' }}>
                                                    <img src={ad.file_path} alt="ad" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={poppins}>
                                                <Link href={ad.redirect_url} target="_blank" rel="noreferrer" sx={{ color: '#1B2559', fontWeight: 600 }}>
                                                    {ad.redirect_url ? ad.redirect_url.substring(0, 30) : 'N/A'}
                                                </Link>
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton color="primary" onClick={() => handleEditAd(ad)} size="small">
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton color="error" onClick={() => handleDeleteAd(ad.ad_id)} size="small">
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>
        </Container>
    );
}